import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { Drawer } from 'antd';
import useAuthStore from '../../store/useAuthStore';
import { updateTheme as updateThemeApi } from '../../api/users.api';
import { MODULE_IDS } from '../../utils/constants';
import CommandPalette from '../common/CommandPalette';

import logo from '../../assests/logo.jpeg';

const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@300;500;700;800&family=Barlow:wght@300;400;500;600;700&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    :root {
      --bg:          #0a0c0f;
      --sidebar:     #0d1117;
      --surface:     #111418;
      --surface-2:   #161b22;
      --border:      #1e2530;
      --border-hi:   #2a3340;
      --accent:      #f59e0b;
      --accent-dim:  rgba(245,158,11,0.12);
      --green:       #22c55e;
      --text-1:      #e8ecf0;
      --text-2:      #9caabf;
      --text-3:      #64748b;
      --radius:      4px;
      --sidebar-w:   252px;
      --sidebar-collapsed: 60px;
      --header-h:    56px;
      --transition:  0.22s ease;
    }

    html, body { height: 100%; background: var(--bg); }
    ::-webkit-scrollbar { width: 4px; height: 4px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: var(--border-hi); border-radius: 4px; }

    @keyframes fadeUp { from { opacity:0; transform:translateY(10px) } to { opacity:1; transform:translateY(0) } }
    @keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
    @keyframes pulse  { 0%,100%{opacity:1} 50%{opacity:0.4} }

    .app-shell {
      font-family: 'Barlow', sans-serif;
      display: flex;
      min-height: 100vh;
      background: var(--bg);
      color: var(--text-1);
    }

    /* ══ SIDEBAR ══ */
    .sidebar {
      width: var(--sidebar-w);
      flex-shrink: 0;
      background: var(--sidebar);
      border-right: 1px solid var(--border);
      display: flex;
      flex-direction: column;
      position: fixed;
      left: 0; top: 0; bottom: 0;
      z-index: 200;
      overflow: hidden;
      transition: width var(--transition);
    }
    .sidebar.collapsed { width: var(--sidebar-collapsed); }

    /* ── Logo bar ── */
    .sidebar-logo {
      height: var(--header-h);
      display: flex;
      align-items: center;
      padding: 0 12px 0 14px;
      border-bottom: 1px solid var(--border);
      flex-shrink: 0;
      overflow: hidden;
      white-space: nowrap;
      gap: 10px;
      position: relative;
    }
    .sidebar-logo::before {
      content: '';
      position: absolute;
      left: 0; top: 10px; bottom: 10px;
      width: 2px;
      background: var(--accent);
      border-radius: 2px;
    }
    .sidebar-logo-img {
      width: 28px; height: 28px;
      border-radius: var(--radius);
      object-fit: contain;
      flex-shrink: 0;
    }
    .sidebar-logo-text { flex: 1; min-width: 0; overflow: hidden; }
    .sidebar-logo-title {
      font-family: 'Barlow Condensed', sans-serif;
      font-size: 15px; font-weight: 700;
      letter-spacing: 1.5px; text-transform: uppercase;
      color: var(--text-1); display: block; line-height: 1.1;
    }
    .sidebar-logo-sub {
      font-size: 10px; font-weight: 500;
      letter-spacing: 0.5px; color: var(--text-3); display: block;
    }

    .collapse-btn {
      flex-shrink: 0;
      width: 26px; height: 26px;
      display: flex; align-items: center; justify-content: center;
      border: 1px solid var(--border-hi);
      border-radius: var(--radius);
      cursor: pointer;
      color: var(--text-3);
      background: var(--surface-2);
      transition: color var(--transition), border-color var(--transition), background var(--transition);
      outline: none;
    }
    .collapse-btn:hover { color: var(--accent); border-color: var(--accent); background: var(--accent-dim); }
    .collapse-btn svg { transition: transform var(--transition); }
    .sidebar.collapsed .collapse-btn svg { transform: rotate(180deg); }

    .sidebar.collapsed .sidebar-logo {
      padding: 0;
      justify-content: center;
      gap: 0;
    }
    .sidebar.collapsed .sidebar-logo-img,
    .sidebar.collapsed .sidebar-logo-text { display: none; }

    /* ── User card ── */
    .sidebar-user {
      padding: 14px;
      border-bottom: 1px solid var(--border);
      display: flex; align-items: center; gap: 10px;
      overflow: hidden; white-space: nowrap; flex-shrink: 0;
    }
    .sidebar-avatar {
      width: 34px; height: 34px;
      border-radius: var(--radius);
      display: flex; align-items: center; justify-content: center;
      font-family: 'Barlow Condensed', sans-serif;
      font-size: 13px; font-weight: 700;
      flex-shrink: 0;
      border: 1px solid var(--border-hi);
    }
    .sidebar-user-name { font-size: 13px; font-weight: 600; color: var(--text-1); display: block; line-height: 1.2; }
    .sidebar-user-plant { font-size: 11px; font-weight: 500; color: var(--text-3); display: block; letter-spacing: 0.5px; }

    /* ── Nav ── */
    .sidebar-nav { flex: 1; overflow-y: auto; overflow-x: hidden; padding: 12px 0; }

    .nav-group { margin-bottom: 18px; }
    .nav-group-label {
      font-size: 10px; font-weight: 700;
      letter-spacing: 2px; text-transform: uppercase;
      color: var(--text-3);
      padding: 0 16px; margin-bottom: 6px;
      display: block; white-space: nowrap; overflow: hidden;
      transition: opacity var(--transition), height var(--transition), margin var(--transition), padding var(--transition);
    }
    .sidebar.collapsed .nav-group-label { opacity: 0; height: 0; margin: 0; padding: 0; }

    .nav-item {
      display: flex; align-items: center; gap: 10px;
      padding: 9px 14px; margin: 1px 8px;
      border-radius: var(--radius);
      cursor: pointer;
      color: var(--text-2); 
      font-size: 14px;
      font-weight: 500;
      white-space: nowrap; overflow: hidden;
      border-left: 2px solid transparent;
      transition: background var(--transition), color var(--transition), border-color var(--transition), padding var(--transition), margin var(--transition), justify-content var(--transition);
      position: relative;
    }
    .nav-item:hover { background: var(--surface-2); color: var(--text-1); }
    .nav-item.active { background: var(--accent-dim); color: var(--accent); border-left-color: var(--accent); font-weight: 600; }

    .nav-item-icon { font-size: 16px; flex-shrink: 0; display: flex; }
    .nav-item-label { flex: 1; overflow: hidden; transition: opacity var(--transition), max-width var(--transition); }

    .sidebar.collapsed .nav-item {
      justify-content: center;
      padding: 10px 0;
      margin: 1px 6px;
      border-left-color: transparent !important;
      gap: 0;
    }
    .sidebar.collapsed .nav-item.active { background: var(--accent-dim); color: var(--accent); }
    .sidebar.collapsed .nav-item-label { opacity: 0; max-width: 0; flex: 0; pointer-events: none; }

    .sidebar.collapsed .nav-item[data-tip]:hover::after {
      content: attr(data-tip);
      position: absolute;
      left: calc(100% + 10px); top: 50%; transform: translateY(-50%);
      background: var(--surface-2); border: 1px solid var(--border-hi);
      color: var(--text-1); font-size: 13px; font-weight: 500;
      padding: 6px 12px; border-radius: var(--radius);
      white-space: nowrap; pointer-events: none; z-index: 9999;
    }

    /* ══ MAIN AREA ══ */
    .main-area {
      flex: 1; display: flex; flex-direction: column; min-width: 0;
      margin-left: var(--sidebar-w);
      transition: margin-left var(--transition);
    }
    .main-area.collapsed { margin-left: var(--sidebar-collapsed); }
    .main-area.mobile    { margin-left: 0; }

    /* ── Header ── */
    .app-header {
      height: var(--header-h);
      background: var(--surface);
      border-bottom: 1px solid var(--border);
      display: flex; align-items: center; justify-content: space-between;
      padding: 0 20px;
      position: sticky; top: 0; z-index: 100; flex-shrink: 0;
    }

    .breadcrumb { display: flex; align-items: center; font-size: 13px; font-weight: 500; }
    .breadcrumb-item { display: flex; align-items: center; color: var(--text-3); cursor: pointer; transition: color 0.15s; }
    .breadcrumb-item:last-child { color: var(--text-2); cursor: default; }
    .breadcrumb-item:hover:not(:last-child) { color: var(--text-2); }
    .breadcrumb-sep { margin: 0 6px; color: var(--text-3); font-size: 11px; }

    .header-right { display: flex; align-items: center; gap: 10px; }

    .kbd-pill {
      display: flex; align-items: center; gap: 5px;
      padding: 4px 10px; border: 1px solid var(--border-hi); border-radius: var(--radius);
      font-size: 11px; font-weight: 600; letter-spacing: 0.5px;
      color: var(--text-3); cursor: pointer; background: var(--surface-2);
      transition: all 0.15s;
    }
    .kbd-pill:hover { border-color: var(--accent); color: var(--accent); }
    .kbd-key { font-size: 10px; padding: 1px 4px; border: 1px solid var(--border-hi); border-radius: 2px; background: var(--border); }

    .theme-dots { display: flex; align-items: center; gap: 5px; }
    .theme-dot { width: 16px; height: 16px; border-radius: 50%; cursor: pointer; border: 2px solid transparent; transition: transform 0.15s, border-color 0.15s; }
    .theme-dot:hover { transform: scale(1.25); }
    .theme-dot.active { border-color: var(--text-1); }

    .icon-btn {
      width: 32px; height: 32px;
      display: flex; align-items: center; justify-content: center;
      border: 1px solid var(--border); border-radius: var(--radius);
      background: transparent; cursor: pointer; color: var(--text-3);
      transition: all 0.15s; outline: none;
    }
    .icon-btn:hover { border-color: var(--border-hi); color: var(--text-2); background: var(--surface-2); }

    .user-chip {
      display: flex; align-items: center; gap: 8px; cursor: pointer;
      padding: 4px 4px 4px 8px; border: 1px solid var(--border); border-radius: 20px;
      transition: all 0.15s; position: relative;
    }
    .user-chip:hover { border-color: var(--border-hi); background: var(--surface-2); }
    .user-chip-name { font-size: 13px; font-weight: 600; color: var(--text-2); }
    .user-chip-avatar {
      width: 26px; height: 26px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-family: 'Barlow Condensed', sans-serif; font-size: 11px; font-weight: 700;
    }

    .user-dropdown {
      position: absolute; top: calc(100% + 8px); right: 0;
      background: var(--surface-2); border: 1px solid var(--border-hi);
      border-radius: var(--radius); min-width: 160px;
      overflow: hidden; z-index: 999;
      animation: fadeUp 0.15s ease;
    }
    .dropdown-item {
      display: flex; align-items: center; gap: 8px;
      padding: 10px 14px; font-size: 13px; font-weight: 500; cursor: pointer;
      color: var(--text-2); transition: background 0.1s;
    }
    .dropdown-item:hover { background: var(--border); color: var(--text-1); }
    .dropdown-item.danger { color: #f87171; }

    /* ── Content Area (White background with Dot Pattern) ── */
    .app-content { 
      flex: 1; 
      padding: 24px; 
      overflow-y: auto; 
      background-color: #ffffff; 
      background-image: radial-gradient(#d1d5db 1px, transparent 1px); 
      background-size: 20px 20px; 
      color: #111827; 
      animation: fadeIn 0.3s ease; 
    }

    .mobile-menu-btn {
      display: flex; align-items: center; justify-content: center;
      width: 34px; height: 34px; border: 1px solid var(--border);
      border-radius: var(--radius); background: transparent;
      cursor: pointer; color: var(--text-2);
    }

    .status-live { display: flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: var(--text-3); }
    .live-dot { width: 6px; height: 6px; background: var(--green); border-radius: 50%; animation: pulse 2s ease-in-out infinite; }

    .page-enter { animation: fadeUp 0.25s ease; }
  `}</style>
);

/* ── Icons ── */
const icons = {
  home:       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z"/><path d="M9 21V12h6v9"/></svg>,
  tag:        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>,
  inbox:      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>,
  calculator: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="10" x2="8" y2="10"/><line x1="12" y1="10" x2="12" y2="10"/><line x1="16" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="8" y2="14"/><line x1="12" y1="14" x2="12" y2="14"/><line x1="16" y1="14" x2="16" y2="14"/><line x1="8" y1="18" x2="12" y2="18"/></svg>,
  apartment:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><rect x="3" y="3" width="6" height="6" rx="1"/><rect x="15" y="3" width="6" height="6" rx="1"/><rect x="9" y="15" width="6" height="6" rx="1"/><line x1="6" y1="9" x2="6" y2="12"/><line x1="18" y1="9" x2="18" y2="12"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="12" y1="12" x2="12" y2="15"/></svg>,
  shop:       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M3 9l1-6h16l1 6"/><path d="M3 9a2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0"/><path d="M5 9v12h14V9"/></svg>,
  cart:       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>,
  tool:       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
  bar:        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  file:       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/></svg>,
  fileDone:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><polyline points="9 15 11 17 15 13"/></svg>,
  pie:        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/></svg>,
  user:       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  shield:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  logout:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  chevLeft:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14"><polyline points="15 18 9 12 15 6"/></svg>,
  menu:       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>,
  search:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
};

const THEME_COLORS = [
  { color: '#f59e0b', name: 'Amber'  },
  { color: '#1a56db', name: 'Blue'   },
  { color: '#0e9f6e', name: 'Green'  },
  { color: '#8b5cf6', name: 'Purple' },
  { color: '#ec4899', name: 'Pink'   },
];

const getInitials = (name) => {
  if (!name) return 'U';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

/* ── Sidebar Content ── */
const SidebarContent = ({ collapsed, isMobile, user, location, navigate, onClose, onToggle }) => {
  const hasAccess = (moduleId) => user?.moduleList?.includes(moduleId);

  const menuGroups = [
    { group: 'DASHBOARD', items: [{ icon: icons.home, label: 'Dashboard', path: '/dashboard' }] },
    {
      group: 'MASTERS',
      items: [
        hasAccess(MODULE_IDS.READY_PRODUCT) && { icon: icons.tag,        label: 'Ready Products', path: '/dashboard/masters/ready-products' },
        hasAccess(MODULE_IDS.RAW_MATERIAL)  && { icon: icons.inbox,      label: 'Raw Materials',  path: '/dashboard/masters/raw-materials' },
        { icon: icons.calculator, label: 'Unit Master', path: '/dashboard/masters/units' },
        hasAccess(MODULE_IDS.MAPPING) && { icon: icons.apartment, label: 'Raw vs Ready', path: '/dashboard/masters/mappings' },
        hasAccess(MODULE_IDS.VENDOR)  && { icon: icons.shop,      label: 'Vendors',     path: '/dashboard/masters/vendors' },
      ].filter(Boolean),
    },
    {
      group: 'TRANSACTIONS',
      items: [
        hasAccess(MODULE_IDS.RAW_PURCHASE) && { icon: icons.cart, label: 'Raw Purchase',     path: '/dashboard/transactions/purchase' },
        hasAccess(MODULE_IDS.PRODUCTION)   && { icon: icons.tool, label: 'Production Entry', path: '/dashboard/transactions/production' },
      ].filter(Boolean),
    },
    hasAccess(MODULE_IDS.REPORTS) && {
      group: 'REPORTS',
      items: [
        { icon: icons.bar,      label: 'Stock Summary',       path: '/dashboard/reports/stock' },
        { icon: icons.file,     label: 'Purchase Register',   path: '/dashboard/reports/purchase' },
        { icon: icons.fileDone, label: 'Production Register', path: '/dashboard/reports/production' },
        { icon: icons.pie,      label: 'Consumption',         path: '/dashboard/reports/consumption' },
      ],
    },
    hasAccess(MODULE_IDS.USER_MGMT) && {
      group: 'ADMIN',
      items: [
        { icon: icons.user,   label: 'Users', path: '/dashboard/admin/users' },
        { icon: icons.shield, label: 'Roles', path: '/dashboard/admin/roles' },
      ],
    },
  ].filter(Boolean);

  const handleNav = (path) => { navigate(path); if (onClose) onClose(); };

  return (
    <>
      {/* Logo bar */}
      <div className="sidebar-logo">
        {(!collapsed || isMobile) ? (
          <>
            <img src={logo} alt="Logo" className="sidebar-logo-img" />
            <div className="sidebar-logo-text">
              <span className="sidebar-logo-title">OceanBlue</span>
              <span className="sidebar-logo-sub">Inventory Mgmt System</span>
            </div>
            {!isMobile && onToggle && (
              <button className="collapse-btn" onClick={onToggle} title="Collapse sidebar">
                {icons.chevLeft}
              </button>
            )}
          </>
        ) : (
          onToggle && (
            <button className="collapse-btn" onClick={onToggle} title="Expand sidebar">
              {icons.chevLeft}
            </button>
          )
        )}
      </div>

      {/* User card */}
      {(!collapsed || isMobile) && (
        <div className="sidebar-user">
          <div
            className="sidebar-avatar"
            style={{ background: user?.themeColor ? user.themeColor + '22' : 'var(--accent-dim)', color: user?.themeColor || 'var(--accent)' }}
          >
            {getInitials(user?.userName || user?.loginId)}
          </div>
          <div style={{ overflow: 'hidden' }}>
            <span className="sidebar-user-name">{user?.userName || user?.loginId}</span>
            <span className="sidebar-user-plant">{user?.plantName}</span>
          </div>
        </div>
      )}

      {/* Nav */}
      <nav className="sidebar-nav">
        {menuGroups.map((group, gi) => (
          <div className="nav-group" key={gi}>
            <span className="nav-group-label">{group.group}</span>
            {group.items.map((item, ii) => {
              const isActive = location.pathname === item.path;
              return (
                <div
                  key={ii}
                  className={`nav-item${isActive ? ' active' : ''}`}
                  data-tip={collapsed && !isMobile ? item.label : undefined}
                  onClick={() => handleNav(item.path)}
                >
                  <span className="nav-item-icon">{item.icon}</span>
                  <span className="nav-item-label">{item.label}</span>
                </div>
              );
            })}
          </div>
        ))}
      </nav>
    </>
  );
};

/* ── Main Component ── */
const AppLayout = () => {
  const [collapsed, setCollapsed]               = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isMobile, setIsMobile]                 = useState(false);
  const [paletteOpen, setPaletteOpen]           = useState(false);
  const [userMenuOpen, setUserMenuOpen]         = useState(false);
  const userChipRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, updateTheme } = useAuthStore();

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (userChipRef.current && !userChipRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => { logout(); navigate('/login', { replace: true }); };

  const handleThemeChange = async (color) => {
    try {
      updateTheme(color);
      if (user?.plantId && user?.loginId) await updateThemeApi(user.plantId, user.loginId, color);
    } catch (_) {}
  };

  const getBreadcrumbs = () => {
    const parts = location.pathname.split('/').filter(Boolean);
    return [
      { label: 'Home', path: '/dashboard' },
      ...parts.slice(1).map((p, i) => ({
        label: p.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        path: '/' + parts.slice(0, i + 2).join('/'),
      })),
    ];
  };

  const crumbs = getBreadcrumbs();

  return (
    <>
      <GlobalStyles />
      <div className="app-shell">

        {/* Desktop Sidebar */}
        {!isMobile && (
          <aside className={`sidebar${collapsed ? ' collapsed' : ''}`}>
            <SidebarContent
              collapsed={collapsed}
              isMobile={false}
              user={user}
              location={location}
              navigate={navigate}
              onToggle={() => setCollapsed(v => !v)}
            />
          </aside>
        )}

        {/* Mobile Drawer */}
        {isMobile && (
          <Drawer
            placement="left"
            onClose={() => setMobileDrawerOpen(false)}
            open={mobileDrawerOpen}
            closable={false}
            width={252}
            styles={{ body: { padding: 0, background: 'var(--sidebar)', overflow: 'hidden' } }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--sidebar)' }}>
              <SidebarContent
                collapsed={false}
                isMobile={true}
                user={user}
                location={location}
                navigate={navigate}
                onClose={() => setMobileDrawerOpen(false)}
              />
            </div>
          </Drawer>
        )}

        {/* Main area */}
        <div className={`main-area${!isMobile && collapsed ? ' collapsed' : ''}${isMobile ? ' mobile' : ''}`}>

          <header className="app-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {isMobile && (
                <button className="mobile-menu-btn" onClick={() => setMobileDrawerOpen(true)}>
                  {icons.menu}
                </button>
              )}
              {!isMobile && (
                <nav className="breadcrumb">
                  {crumbs.map((c, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && <span className="breadcrumb-sep">/</span>}
                      <span className="breadcrumb-item" onClick={() => i < crumbs.length - 1 && navigate(c.path)}>
                        {i === 0 ? icons.home : c.label}
                      </span>
                    </React.Fragment>
                  ))}
                </nav>
              )}
            </div>

            <div className="header-right">
              {!isMobile && <div className="status-live"><span className="live-dot" />Live</div>}
              {!isMobile && (
                <div className="kbd-pill" onClick={() => setPaletteOpen(true)}>
                  {icons.search}<span>Search</span><span className="kbd-key">⌘K</span>
                </div>
              )}
              {!isMobile && (
                <div className="theme-dots">
                  {THEME_COLORS.map(t => (
                    <div
                      key={t.color}
                      className={`theme-dot${user?.themeColor === t.color ? ' active' : ''}`}
                      style={{ background: t.color }}
                      title={t.name}
                      onClick={() => handleThemeChange(t.color)}
                    />
                  ))}
                </div>
              )}
              {!isMobile && <div style={{ width: 1, height: 20, background: 'var(--border)' }} />}

              <div className="user-chip" ref={userChipRef} onClick={() => setUserMenuOpen(v => !v)}>
                {!isMobile && <span className="user-chip-name">{user?.userName || user?.loginId}</span>}
                <div
                  className="user-chip-avatar"
                  style={{ background: user?.themeColor ? user.themeColor + '22' : 'var(--accent-dim)', color: user?.themeColor || 'var(--accent)' }}
                >
                  {getInitials(user?.userName || user?.loginId)}
                </div>
                {userMenuOpen && (
                  <div className="user-dropdown">
                    <div style={{ padding: '10px 14px 8px', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>{user?.userName}</div>
                      <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-3)' }}>{user?.plantName}</div>
                    </div>
                    <div className="dropdown-item danger" onClick={handleLogout}>
                      {icons.logout} Logout
                    </div>
                  </div>
                )}
              </div>
            </div>
          </header>

          <main className="app-content">
            <div className="page-enter"><Outlet /></div>
          </main>
        </div>

        <CommandPalette isOpen={paletteOpen} onClose={() => setPaletteOpen(false)} currentPageActions={[]} />
      </div>
    </>
  );
};

export default AppLayout;